import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import moment from 'moment-jalaali';
import PersianDatePicker from '../components/PersianDatePicker';
import LoadingSpinner from '../components/common/LoadingSpinner';
import { formatRial } from '../utils/formatMoneyFa';
import { getApiBaseUrl } from '../utils/getApiUrl';
import { canAccessCustomerClub } from '../utils/customerClubAccess';

type DatePreset = 'today' | 'month' | 'last7' | 'custom';
type ReportTab = 'cash' | 'refund' | 'days' | 'initial_waiting' | 'done_unpaid';

interface CashSummary {
  cash_total: number;
  cash_initial: number;
  cash_followup: number;
  cash_count: number;
  refund_total: number;
  refund_count: number;
  retained_total: number;
  fee_refund_count: number;
  net_cash: number;
  unpaid_remain_total?: number;
  unpaid_remain_count?: number;
  initial_waiting_total?: number;
  initial_waiting_count?: number;
  done_unpaid_count?: number;
  done_unpaid_fran_total?: number;
}

interface CashRow {
  database?: string;
  cash_id?: number;
  cash_day?: string;
  cash_date: string;
  patient_id?: number;
  patient_rec_code: string;
  full_name: string;
  cashier_name: string;
  cash_register: string;
  amount: number;
  kind: string;
  kind_label: string;
  paid_status?: string;
  paid_label?: string;
  is_done?: boolean;
  work_status?: string;
  work_status_label?: string;
  recv_rank?: number;
  prior_received?: number;
  total_refunded?: number;
  retained_amount?: number;
  has_fee?: boolean;
  fran?: number;
  bime?: number;
  cash_recv?: number;
  insu_paye_name?: string;
  operator_name?: string;
  note?: string;
}

interface DayRow {
  date: string;
  cash_total: number;
  cash_initial: number;
  cash_followup: number;
  cash_count: number;
  refund_total: number;
  refund_count: number;
  retained_total: number;
  net_cash: number;
}

interface CashReport {
  from_date: string;
  to_date: string;
  tab: ReportTab;
  summary: CashSummary;
  days: DayRow[];
  total: number;
  page: number;
  page_size: number;
  pages: number;
  rows: CashRow[];
}

interface ReceptionCashRow {
  cash_id?: number;
  cash_date: string;
  amount: number;
  cashier_name: string;
  cash_register?: string;
  kind?: string;
  kind_label?: string;
  paid_status?: string;
  paid_label?: string;
}

interface ReceptionService {
  line_id?: number;
  service: string;
  machine: string;
  bime: number;
  fran: number;
  total: number;
  status: string;
  status_label: string;
  dowork_date?: string;
}

interface ReceptionDetail {
  database: string;
  patient_id: number;
  patient_rec_code: string;
  recept_date: string;
  full_name: string;
  phone?: string;
  national_id?: string;
  insu_paye_name: string;
  insu_takmili_name: string;
  doctor_name?: string;
  operator_name?: string;
  note?: string;
  status: string;
  status_label: string;
  naghdi: number;
  bime: number;
  fran: number;
  payable: number;
  locked?: boolean;
  services: ReceptionService[];
  cash_rows: ReceptionCashRow[];
}

const PAGE_SIZE = 50;

const toPersianDigits = (s: string | number): string =>
  String(s).replace(/\d/g, (d) => '۰۱۲۳۴۵۶۷۸۹'[Number(d)]);

const DATE_PRESETS: { id: DatePreset; label: string; apply: () => { from: string; to: string } }[] = [
  {
    id: 'today',
    label: 'امروز',
    apply: () => {
      const d = moment().format('jYYYY/jMM/jDD');
      return { from: d, to: d };
    },
  },
  {
    id: 'month',
    label: 'ماه جاری',
    apply: () => ({
      from: moment().startOf('jMonth').format('jYYYY/jMM/jDD'),
      to: moment().format('jYYYY/jMM/jDD'),
    }),
  },
  {
    id: 'last7',
    label: '۷ روز اخیر',
    apply: () => ({
      from: moment().subtract(6, 'day').format('jYYYY/jMM/jDD'),
      to: moment().format('jYYYY/jMM/jDD'),
    }),
  },
];

const TABS: { id: ReportTab; label: string }[] = [
  { id: 'cash', label: 'نقدی (اولیه / تکمیل)' },
  { id: 'initial_waiting', label: 'نقدی اولیه و انجام‌نشده' },
  { id: 'done_unpaid', label: 'نقدی انجام‌شده بدون پرداخت' },
  { id: 'refund', label: 'استرداد روزانه' },
  { id: 'days', label: 'خلاصه روزانه' },
];

const paidBadgeClass = (status?: string): string => {
  if (status === 'paid_out') return 'bg-emerald-50 text-emerald-800 ring-1 ring-emerald-200';
  if (status === 'received') return 'bg-sky-50 text-sky-800 ring-1 ring-sky-200';
  if (status === 'unpaid') return 'bg-rose-50 text-rose-800 ring-1 ring-rose-200';
  return 'bg-slate-50 text-slate-600 ring-1 ring-slate-200';
};

const AdminCustomerClubCash: React.FC = () => {
  const navigate = useNavigate();
  const [fromDate, setFromDate] = useState(() => moment().startOf('jMonth').format('jYYYY/jMM/jDD'));
  const [toDate, setToDate] = useState(() => moment().format('jYYYY/jMM/jDD'));
  const [datePreset, setDatePreset] = useState<DatePreset>('month');
  const [tab, setTab] = useState<ReportTab>('cash');
  const [page, setPage] = useState(1);
  const [report, setReport] = useState<CashReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);
  const [detail, setDetail] = useState<ReceptionDetail | null>(null);

  const authHeaders = useCallback(() => {
    const token = localStorage.getItem('admin_token');
    return { Authorization: `Bearer ${token}` };
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin/login');
      return;
    }
    if (!canAccessCustomerClub()) navigate('/admin/dashboard');
  }, [navigate]);

  useEffect(() => {
    if (!detailOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setDetailOpen(false);
        setDetail(null);
        setDetailError(null);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [detailOpen]);

  const applyDatePreset = (preset: DatePreset) => {
    const found = DATE_PRESETS.find((item) => item.id === preset);
    if (!found) return;
    const next = found.apply();
    setFromDate(next.from);
    setToDate(next.to);
    setDatePreset(preset);
    setPage(1);
    setReport(null);
  };

  const fetchReport = useCallback(
    async (nextPage = page, nextTab = tab) => {
      if (!fromDate || !toDate) {
        setError('بازه تاریخ را کامل کنید');
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const params = new URLSearchParams({
          from_date: fromDate,
          to_date: toDate,
          tab: nextTab,
          page: String(nextPage),
          page_size: String(PAGE_SIZE),
        });
        const res = await fetch(`${getApiBaseUrl()}/admin/customer-club/cash-report?${params}`, {
          headers: authHeaders(),
        });
        const body = await res.json().catch(() => ({}));
        if (!res.ok) {
          throw new Error(typeof body.detail === 'string' ? body.detail : 'خطا در دریافت گزارش');
        }
        setReport(body as CashReport);
        setPage(Number(body.page) || nextPage);
      } catch (err: unknown) {
        setReport(null);
        setError(err instanceof Error ? err.message : 'خطا در دریافت گزارش');
      } finally {
        setLoading(false);
      }
    },
    [authHeaders, fromDate, toDate, page, tab]
  );

  useEffect(() => {
    void fetchReport(1, tab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openDetail = async (row: CashRow) => {
    if (!row.patient_id) return;
    setDetailOpen(true);
    setDetailLoading(true);
    setDetailError(null);
    setDetail(null);
    try {
      const params = new URLSearchParams({ patient_id: String(row.patient_id) });
      if (row.database) params.set('database', row.database);
      const res = await fetch(
        `${getApiBaseUrl()}/admin/customer-club/watchdog/reception-detail?${params}`,
        { headers: authHeaders() }
      );
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(typeof body.detail === 'string' ? body.detail : 'خطا در دریافت ریز پذیرش');
      }
      setDetail(body as ReceptionDetail);
    } catch (err: unknown) {
      setDetailError(err instanceof Error ? err.message : 'خطا در دریافت ریز پذیرش');
    } finally {
      setDetailLoading(false);
    }
  };

  const summary = report?.summary;
  const pages = report?.pages ?? 1;

  return (
    <div className="w-full space-y-4" dir="rtl">
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-l from-amber-600 via-amber-700 to-slate-800 px-5 py-4 text-white shadow-lg shadow-amber-900/20 sm:px-6">
        <div className="relative">
          <button
            type="button"
            onClick={() => navigate('/admin/customer-club')}
            className="mb-1 text-[11px] text-amber-100 hover:text-white"
          >
            ← بازگشت به باشگاه مشتریان
          </button>
          <p className="text-xs font-medium uppercase tracking-wider text-amber-100/90">صندوق HIS</p>
          <h1 className="mt-0.5 text-xl font-bold tracking-tight sm:text-2xl">گزارش نقدی و استرداد</h1>
          <p className="mt-1 max-w-2xl text-xs text-amber-50/90">
            روی هر ردیف کلیک کنید تا ریز پذیرش باز شود. ثبت نقدی = پول از بیمار گرفته شده؛ ثبت استرداد =
            پول به بیمار پرداخت شده. اگر مانده‌ای هنوز در HIS باشد، جدا در کارت «مانده پرداخت‌نشده» دیده می‌شود.
          </p>
        </div>
      </div>

      <div className="rounded-xl border border-slate-200/80 bg-white p-2 shadow-sm sm:p-2.5">
        <div className="flex flex-col gap-2 lg:flex-row lg:items-center">
          <div className="flex flex-wrap items-center gap-1.5">
            <div className="flex gap-0.5 rounded-lg bg-slate-100 p-0.5">
              {DATE_PRESETS.map((preset) => (
                <button
                  key={preset.id}
                  type="button"
                  onClick={() => applyDatePreset(preset.id)}
                  className={`rounded-md px-2 py-1 text-[11px] font-medium ${
                    datePreset === preset.id ? 'bg-white text-amber-800 shadow-sm' : 'text-slate-600'
                  }`}
                >
                  {preset.label}
                </button>
              ))}
            </div>
            <div className="flex items-center overflow-hidden rounded-lg border border-slate-200 bg-slate-50/60">
              <div className="w-[7.25rem] border-l border-slate-200">
                <PersianDatePicker
                  compact
                  borderless
                  value={fromDate}
                  placeholder="از"
                  onChange={(value) => {
                    setFromDate(value);
                    setDatePreset('custom');
                    setPage(1);
                  }}
                />
              </div>
              <div className="w-[7.25rem]">
                <PersianDatePicker
                  compact
                  borderless
                  value={toDate}
                  placeholder="تا"
                  onChange={(value) => {
                    setToDate(value);
                    setDatePreset('custom');
                    setPage(1);
                  }}
                />
              </div>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-1.5 lg:mr-auto">
            <div className="flex gap-0.5 rounded-lg bg-slate-100 p-0.5">
              {TABS.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => {
                    setTab(item.id);
                    setPage(1);
                    void fetchReport(1, item.id);
                  }}
                  className={`rounded-md px-2 py-1 text-[11px] font-medium ${
                    tab === item.id ? 'bg-white text-amber-800 shadow-sm' : 'text-slate-600'
                  }`}
                >
                  {item.label}
                </button>
              ))}
            </div>
            <button
              type="button"
              onClick={() => void fetchReport(1, tab)}
              disabled={loading}
              className="inline-flex h-8 items-center rounded-lg bg-amber-600 px-3 text-[11px] font-semibold text-white hover:bg-amber-700 disabled:opacity-50"
            >
              {loading ? 'در حال خواندن...' : 'اجرا'}
            </button>
          </div>
        </div>
      </div>

      {error ? (
        <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800">{error}</div>
      ) : null}

      {summary ? (
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-8">
          <div className="rounded-xl border border-amber-100 bg-amber-50/70 px-3 py-2.5">
            <p className="text-[10px] text-amber-800/80">جمع نقدی</p>
            <p className="mt-0.5 text-sm font-bold tabular-nums text-amber-950">{formatRial(summary.cash_total)}</p>
          </div>
          <div className="rounded-xl border border-orange-100 bg-orange-50/70 px-3 py-2.5">
            <p className="text-[10px] text-orange-800/80">نقدی اولیه</p>
            <p className="mt-0.5 text-sm font-bold tabular-nums text-orange-950">{formatRial(summary.cash_initial)}</p>
          </div>
          <div className="rounded-xl border border-yellow-100 bg-yellow-50/70 px-3 py-2.5">
            <p className="text-[10px] text-yellow-900/80">نقدی تکمیل</p>
            <p className="mt-0.5 text-sm font-bold tabular-nums text-yellow-950">{formatRial(summary.cash_followup)}</p>
          </div>
          <div className="rounded-xl border border-teal-100 bg-teal-50/70 px-3 py-2.5">
            <p className="text-[10px] text-teal-800/80">اولیه و انجام‌نشده</p>
            <p className="mt-0.5 text-sm font-bold tabular-nums text-teal-950">
              {formatRial(summary.initial_waiting_total || 0)}
            </p>
            <p className="text-[10px] text-teal-700/70">
              {toPersianDigits(summary.initial_waiting_count || 0)} پذیرش
            </p>
          </div>
          <div className="rounded-xl border border-rose-200 bg-rose-50/80 px-3 py-2.5">
            <p
              className="text-[10px] text-rose-800/80"
              title="بیمار نقدی/آزاد؛ کار انجام شده؛ هیچ دریافتی صندوق ثبت نشده"
            >
              نقدی انجام‌شده بدون پرداخت
            </p>
            <p className="mt-0.5 text-sm font-bold tabular-nums text-rose-950">
              {formatRial(summary.done_unpaid_fran_total || 0)}
            </p>
            <p className="text-[10px] text-rose-700/70">
              {toPersianDigits(summary.done_unpaid_count || 0)} پذیرش
            </p>
          </div>
          <div className="rounded-xl border border-rose-100 bg-rose-50/70 px-3 py-2.5">
            <p className="text-[10px] text-rose-800/80">مبلغ برگشت‌داده‌شده</p>
            <p className="mt-0.5 text-sm font-bold tabular-nums text-rose-950">{formatRial(summary.refund_total)}</p>
            <p className="text-[10px] text-rose-700/70">{toPersianDigits(summary.refund_count)} مورد</p>
          </div>
          <div className="rounded-xl border border-violet-100 bg-violet-50/70 px-3 py-2.5">
            <p
              className="text-[10px] text-violet-800/80"
              title="نقدی بیمار منهای مبلغ برگشت‌داده‌شده — مبلغی که به بیمار برنگشته"
            >
              مبلغ برگشت‌نداده
            </p>
            <p className="mt-0.5 text-sm font-bold tabular-nums text-violet-950">{formatRial(summary.retained_total)}</p>
          </div>
          <div className="rounded-xl border border-fuchsia-100 bg-fuchsia-50/70 px-3 py-2.5">
            <p
              className="text-[10px] text-fuchsia-800/80"
              title="اعتبار باز در HIS که هنوز به بیمار پرداخت نشده"
            >
              مانده پرداخت‌نشده
            </p>
            <p className="mt-0.5 text-sm font-bold tabular-nums text-fuchsia-950">
              {formatRial(summary.unpaid_remain_total || 0)}
            </p>
            <p className="text-[10px] text-fuchsia-700/70">
              {toPersianDigits(summary.unpaid_remain_count || 0)} نفر
            </p>
          </div>
          <div className="rounded-xl border border-emerald-100 bg-emerald-50/70 px-3 py-2.5">
            <p className="text-[10px] text-emerald-800/80">خالص صندوق</p>
            <p className="mt-0.5 text-sm font-bold tabular-nums text-emerald-950">{formatRial(summary.net_cash)}</p>
          </div>
        </div>
      ) : null}

      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
        {loading && !report ? (
          <div className="py-16">
            <LoadingSpinner text="در حال بارگذاری گزارش صندوق..." />
          </div>
        ) : !report ? (
          <div className="px-4 py-12 text-center text-sm text-slate-500">بازه را انتخاب کنید و «اجرا» را بزنید.</div>
        ) : tab === 'days' ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-100 text-sm">
              <thead className="bg-slate-50 text-[11px] text-slate-500">
                <tr>
                  <th className="px-3 py-2.5 text-right font-medium">تاریخ</th>
                  <th className="px-3 py-2.5 text-right font-medium">نقدی اولیه</th>
                  <th className="px-3 py-2.5 text-right font-medium">نقدی تکمیل</th>
                  <th className="px-3 py-2.5 text-right font-medium">جمع نقدی</th>
                  <th className="px-3 py-2.5 text-right font-medium">استرداد</th>
                  <th
                    className="px-3 py-2.5 text-right font-medium"
                    title="نقدی بیمار − مبلغ برگشت‌داده‌شده؛ مبلغی که به بیمار برنگشته"
                  >
                    مبلغ برگشت‌نداده
                  </th>
                  <th className="px-3 py-2.5 text-right font-medium">خالص</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {(report.days || []).map((row) => (
                  <tr key={row.date} className="hover:bg-amber-50/40">
                    <td className="whitespace-nowrap px-3 py-2 tabular-nums">{toPersianDigits(row.date)}</td>
                    <td className="whitespace-nowrap px-3 py-2 tabular-nums">{formatRial(row.cash_initial)}</td>
                    <td className="whitespace-nowrap px-3 py-2 tabular-nums">{formatRial(row.cash_followup)}</td>
                    <td className="whitespace-nowrap px-3 py-2 tabular-nums font-medium">{formatRial(row.cash_total)}</td>
                    <td className="whitespace-nowrap px-3 py-2 tabular-nums text-rose-700">
                      {formatRial(row.refund_total)}
                      {row.refund_count > 0 ? (
                        <span className="mr-1 text-[10px] text-rose-500">
                          ({toPersianDigits(row.refund_count)})
                        </span>
                      ) : null}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 tabular-nums text-violet-700">
                      {formatRial(row.retained_total)}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 tabular-nums font-medium text-emerald-800">
                      {formatRial(row.net_cash)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (report.rows || []).length === 0 ? (
          <div className="px-4 py-12 text-center text-sm text-slate-500">ردیفی در این بازه نیست.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-100 text-sm">
              <thead className="bg-slate-50 text-[11px] text-slate-500">
                <tr>
                  <th className="px-3 py-2.5 text-right font-medium">
                    {tab === 'done_unpaid' ? 'تاریخ پذیرش' : 'تاریخ'}
                  </th>
                  <th className="px-3 py-2.5 text-right font-medium">کد پذیرش</th>
                  <th className="px-3 py-2.5 text-right font-medium">بیمار</th>
                  {tab === 'done_unpaid' ? (
                    <>
                      <th className="px-3 py-2.5 text-right font-medium">ثبت‌کننده</th>
                      <th className="px-3 py-2.5 text-right font-medium">یادداشت</th>
                      <th className="px-3 py-2.5 text-right font-medium">وضعیت پول</th>
                      <th
                        className="px-3 py-2.5 text-right font-medium"
                        title="مبلغ بیمار نقدی که باید می‌پرداخت و در صندوق ثبت نشده"
                      >
                        مبلغ پرداخت‌نشده
                      </th>
                    </>
                  ) : (
                    <>
                      <th className="px-3 py-2.5 text-right font-medium">نوع</th>
                      {tab === 'initial_waiting' ? (
                        <th className="px-3 py-2.5 text-right font-medium">وضعیت کار</th>
                      ) : null}
                      <th className="px-3 py-2.5 text-right font-medium">وضعیت پول</th>
                      <th className="px-3 py-2.5 text-right font-medium">صندوق / صندوقدار</th>
                      <th className="px-3 py-2.5 text-right font-medium">
                        {tab === 'refund' ? 'مبلغ برگشت‌داده‌شده' : 'مبلغ'}
                      </th>
                      {tab === 'refund' ? (
                        <>
                          <th
                            className="px-3 py-2.5 text-right font-medium"
                            title="جمع نقدی‌هایی که همین پذیرش به صندوق پرداخته (قبل از استرداد)"
                          >
                            نقدی پرداختی بیمار
                          </th>
                          <th
                            className="px-3 py-2.5 text-right font-medium"
                            title="نقدی پرداختی بیمار منهای مبلغ برگشت‌داده‌شده؛ اگر صفر باشد یعنی همه را برگردانده‌ایم"
                          >
                            مبلغ برگشت‌نداده
                          </th>
                        </>
                      ) : null}
                    </>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {report.rows.map((row, idx) => (
                  <tr
                    key={`${row.cash_id || row.patient_id}-${idx}`}
                    className="cursor-pointer hover:bg-amber-50/60"
                    onClick={() => void openDetail(row)}
                    title="کلیک برای ریز پذیرش"
                  >
                    <td className="whitespace-nowrap px-3 py-2 tabular-nums text-slate-600">
                      {toPersianDigits(row.cash_date || '—')}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 tabular-nums">
                      {toPersianDigits(row.patient_rec_code || '—')}
                    </td>
                    <td className="max-w-[10rem] truncate px-3 py-2">{row.full_name || '—'}</td>
                    {tab === 'done_unpaid' ? (
                      <>
                        <td className="max-w-[9rem] truncate px-3 py-2 text-slate-700" title={row.operator_name}>
                          {row.operator_name || '—'}
                        </td>
                        <td className="max-w-[14rem] truncate px-3 py-2 text-slate-600" title={row.note || undefined}>
                          {row.note ? row.note : '—'}
                        </td>
                        <td className="whitespace-nowrap px-3 py-2">
                          <span
                            className={`inline-flex rounded-md px-2 py-0.5 text-[11px] font-medium ${paidBadgeClass(row.paid_status)}`}
                          >
                            {row.paid_label || 'پول نداده'}
                          </span>
                        </td>
                        <td className="whitespace-nowrap px-3 py-2 text-left tabular-nums font-medium text-rose-800">
                          {formatRial(row.fran ?? row.amount)}
                        </td>
                      </>
                    ) : (
                      <>
                        <td className="whitespace-nowrap px-3 py-2">
                          <span
                            className={`inline-flex rounded-md px-2 py-0.5 text-[11px] font-medium ${
                              row.kind === 'initial'
                                ? 'bg-orange-50 text-orange-800 ring-1 ring-orange-200'
                                : row.kind === 'followup'
                                  ? 'bg-yellow-50 text-yellow-900 ring-1 ring-yellow-200'
                                  : 'bg-rose-50 text-rose-800 ring-1 ring-rose-200'
                            }`}
                          >
                            {row.kind_label}
                          </span>
                        </td>
                        {tab === 'initial_waiting' ? (
                          <td className="whitespace-nowrap px-3 py-2">
                            <span className="inline-flex rounded-md bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-900 ring-1 ring-amber-200">
                              {row.work_status_label || 'در انتظار انجام'}
                            </span>
                          </td>
                        ) : null}
                        <td className="whitespace-nowrap px-3 py-2">
                          <span
                            className={`inline-flex rounded-md px-2 py-0.5 text-[11px] font-medium ${paidBadgeClass(row.paid_status)}`}
                          >
                            {row.paid_label || '—'}
                          </span>
                        </td>
                        <td className="max-w-[12rem] truncate px-3 py-2 text-slate-600" title={row.cashier_name}>
                          {row.cash_register || '—'}
                          {row.cashier_name ? ` · ${row.cashier_name}` : ''}
                        </td>
                        <td className="whitespace-nowrap px-3 py-2 text-left tabular-nums font-medium">
                          {formatRial(row.amount)}
                        </td>
                        {tab === 'refund' ? (
                          <>
                            <td className="whitespace-nowrap px-3 py-2 text-left tabular-nums">
                              {formatRial(row.prior_received || 0)}
                            </td>
                            <td className="whitespace-nowrap px-3 py-2 text-left tabular-nums text-violet-800">
                              {row.has_fee ? formatRial(row.retained_amount || 0) : '—'}
                            </td>
                          </>
                        ) : null}
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {report && report.total > 0 && tab !== 'days' ? (
          <div className="flex flex-wrap items-center justify-between gap-2 border-t border-slate-100 bg-slate-50/80 px-4 py-3 text-sm">
            <p className="text-xs text-slate-500">
              صفحه {toPersianDigits(page)} از {toPersianDigits(pages)} · {toPersianDigits(report.total)} ردیف
              {' · '}برای ریز پذیرش روی ردیف کلیک کنید
            </p>
            <div className="flex gap-2">
              <button
                type="button"
                disabled={page <= 1 || loading}
                onClick={() => {
                  const next = Math.max(1, page - 1);
                  setPage(next);
                  void fetchReport(next, tab);
                }}
                className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-slate-700 enabled:hover:bg-slate-100 disabled:opacity-40"
              >
                قبلی
              </button>
              <button
                type="button"
                disabled={page >= pages || loading}
                onClick={() => {
                  const next = Math.min(pages, page + 1);
                  setPage(next);
                  void fetchReport(next, tab);
                }}
                className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-slate-700 enabled:hover:bg-slate-100 disabled:opacity-40"
              >
                بعدی
              </button>
            </div>
          </div>
        ) : null}
      </div>

      {detailOpen
        ? createPortal(
            <div className="fixed inset-0 z-[95] flex items-center justify-center p-3 sm:p-6">
              <button
                type="button"
                className="absolute inset-0 bg-slate-950/45"
                aria-label="بستن"
                onClick={() => {
                  setDetailOpen(false);
                  setDetail(null);
                  setDetailError(null);
                }}
              />
              <div
                className="relative flex max-h-[90vh] w-full max-w-3xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl ring-1 ring-amber-100"
                dir="rtl"
                onClick={(e) => e.stopPropagation()}
              >
                <header className="flex items-start justify-between gap-3 border-b border-slate-100 bg-gradient-to-l from-amber-50 to-white px-5 py-4">
                  <div>
                    <p className="text-xs text-amber-800/80">ریز پذیرش / صندوق</p>
                    <h3 className="mt-0.5 text-lg font-bold text-slate-900">
                      {detail?.full_name || (detailLoading ? '...' : '—')}
                    </h3>
                    <p className="mt-1 text-xs text-slate-500">
                      کد پذیرش {toPersianDigits(detail?.patient_rec_code || '—')}
                      {detail?.recept_date ? ` · ${toPersianDigits(detail.recept_date)}` : ''}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setDetailOpen(false);
                      setDetail(null);
                      setDetailError(null);
                    }}
                    className="rounded-lg px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-100"
                  >
                    بستن
                  </button>
                </header>
                <div className="min-h-0 flex-1 overflow-auto px-5 py-4">
                  {detailLoading ? (
                    <div className="py-14">
                      <LoadingSpinner text="در حال بارگذاری ریز پذیرش..." />
                    </div>
                  ) : detailError ? (
                    <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-6 text-center text-sm text-red-800">
                      {detailError}
                    </div>
                  ) : detail ? (
                    <div className="space-y-5">
                      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                        <div className="rounded-xl border border-amber-100 bg-amber-50/50 px-3 py-2.5">
                          <p className="text-[11px] text-amber-800/80">نقدی صندوق</p>
                          <p className="mt-1 text-sm font-bold tabular-nums text-amber-900">
                            {formatRial(detail.naghdi)}
                          </p>
                        </div>
                        <div className="rounded-xl border border-sky-100 bg-sky-50/50 px-3 py-2.5">
                          <p className="text-[11px] text-sky-800/80">بیمه</p>
                          <p className="mt-1 text-sm font-bold tabular-nums text-sky-900">
                            {formatRial(detail.bime)}
                          </p>
                        </div>
                        <div className="rounded-xl border border-violet-100 bg-violet-50/50 px-3 py-2.5">
                          <p className="text-[11px] text-violet-800/80">فرانشیز</p>
                          <p className="mt-1 text-sm font-bold tabular-nums text-violet-900">
                            {formatRial(detail.fran)}
                          </p>
                        </div>
                        <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5">
                          <p className="text-[11px] text-slate-600">قابل پرداخت</p>
                          <p className="mt-1 text-sm font-bold tabular-nums text-slate-900">
                            {formatRial(detail.payable)}
                          </p>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 gap-2 text-sm sm:grid-cols-2">
                        <p>
                          <span className="text-slate-500">ثبت‌کننده پذیرش: </span>
                          <span className="font-medium text-slate-800">
                            {detail.operator_name || '—'}
                          </span>
                        </p>
                        <p>
                          <span className="text-slate-500">پزشک: </span>
                          <span className="font-medium text-slate-800">
                            {detail.doctor_name || '—'}
                          </span>
                        </p>
                      </div>

                      <div className="rounded-xl border border-amber-100 bg-amber-50/40 px-3 py-2.5">
                        <p className="text-[11px] font-medium text-amber-900">یادداشت پذیرش</p>
                        <p className="mt-1 whitespace-pre-wrap text-sm leading-6 text-slate-800">
                          {detail.note?.trim() ? detail.note : 'یادداشتی ثبت نشده'}
                        </p>
                      </div>

                      <section>
                        <h4 className="text-sm font-bold text-slate-900">ردیف‌های صندوق</h4>
                        {(detail.cash_rows || []).length === 0 ? (
                          <p className="mt-2 text-sm text-slate-500">ردیفی ثبت نشده.</p>
                        ) : (
                          <div className="mt-2 overflow-x-auto rounded-xl border border-slate-100">
                            <table className="min-w-full divide-y divide-slate-100 text-sm">
                              <thead className="bg-slate-50 text-[11px] text-slate-500">
                                <tr>
                                  <th className="px-3 py-2 text-right font-medium">تاریخ</th>
                                  <th className="px-3 py-2 text-right font-medium">نوع</th>
                                  <th className="px-3 py-2 text-right font-medium">وضعیت پول</th>
                                  <th className="px-3 py-2 text-right font-medium">صندوق‌دار</th>
                                  <th className="px-3 py-2 text-right font-medium">مبلغ</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-slate-50">
                                {detail.cash_rows.map((cash, i) => (
                                  <tr key={`${cash.cash_id}-${i}`}>
                                    <td className="whitespace-nowrap px-3 py-2 tabular-nums text-slate-600">
                                      {toPersianDigits(cash.cash_date || '—')}
                                    </td>
                                    <td className="whitespace-nowrap px-3 py-2 text-slate-700">
                                      {cash.kind_label || '—'}
                                    </td>
                                    <td className="whitespace-nowrap px-3 py-2">
                                      <span
                                        className={`inline-flex rounded-md px-2 py-0.5 text-[11px] font-medium ${paidBadgeClass(cash.paid_status)}`}
                                      >
                                        {cash.paid_label || '—'}
                                      </span>
                                    </td>
                                    <td className="px-3 py-2 text-slate-700">
                                      {cash.cashier_name || '—'}
                                      {cash.cash_register ? ` · ${cash.cash_register}` : ''}
                                    </td>
                                    <td className="whitespace-nowrap px-3 py-2 tabular-nums font-medium text-amber-900">
                                      {formatRial(cash.amount)}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </section>

                      {(detail.services || []).length > 0 ? (
                        <section>
                          <h4 className="text-sm font-bold text-slate-900">خدمات</h4>
                          <div className="mt-2 overflow-x-auto rounded-xl border border-slate-100">
                            <table className="min-w-full divide-y divide-slate-100 text-sm">
                              <thead className="bg-slate-50 text-[11px] text-slate-500">
                                <tr>
                                  <th className="px-3 py-2 text-right font-medium">خدمت</th>
                                  <th className="px-3 py-2 text-right font-medium">وضعیت</th>
                                  <th className="px-3 py-2 text-right font-medium">جمع</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-slate-50">
                                {detail.services.map((svc) => (
                                  <tr key={svc.line_id}>
                                    <td className="max-w-[14rem] truncate px-3 py-2">
                                      {svc.service || svc.machine || '—'}
                                    </td>
                                    <td className="whitespace-nowrap px-3 py-2 text-slate-600">
                                      {svc.status_label}
                                    </td>
                                    <td className="whitespace-nowrap px-3 py-2 tabular-nums font-medium">
                                      {formatRial(svc.total)}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </section>
                      ) : null}
                    </div>
                  ) : null}
                </div>
              </div>
            </div>,
            document.body
          )
        : null}
    </div>
  );
};

export default AdminCustomerClubCash;
